// Blogs.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { 
  Calendar, 
  Clock, 
  BookOpen,
  ArrowRight
} from "lucide-react";

const Blogs = () => {
  const blogs = [
    {
      id: 1,
      slug: "10-essential-tips-for-first-time-home-buyers",
      title: "10 Essential Tips for First-Time Home Buyers",
      excerpt: "Buying your first home is exciting but can be overwhelming. Discover essential tips to navigate the real estate market confidently and make informed decisions.",
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=500&fit=crop",
      author: "Sarah Johnson",
      date: "March 15, 2024",
      updatedDate: "March 15, 2024",
      readTime: "8 min read",
      category: "Buying Tips",
      tags: ["first time buyer", "real estate tips", "home buying", "mortgage"]
    },
    {
      id: 2,
      slug: "maximizing-property-value-renovation-roi-guide",
      title: "Maximizing Your Property's Value: Renovation ROI Guide",
      excerpt: "Not all renovations are created equal. Learn which home improvements offer the best return on investment and how to prioritize your renovation projects.",
      image: "https://static.vecteezy.com/system/resources/previews/027/477/384/non_2x/stock-graph-technology-background-it-is-a-technology-that-shows-the-growth-price-of-a-company-on-the-stock-market-increasing-the-attractiveness-of-investors-green-and-red-charts-along-with-bars-vector.jpg",
      author: "Michael Chen",
      date: "March 10, 2024",
      updatedDate: "March 10, 2024",
      readTime: "6 min read",
      category: "Investment",
      tags: ["home renovation", "ROI", "property value", "remodeling"]
    },
    {
      id: 3,
      slug: "understanding-real-estate-market-cycles",
      title: "Understanding Real Estate Market Cycles",
      excerpt: "Real estate markets move in cycles. Understanding these patterns can help you make better investment decisions and time your purchases or sales strategically.",
      image: "https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=800&h=500&fit=crop",
      author: "David Williams",
      date: "March 5, 2024",
      updatedDate: "March 5, 2024",
      readTime: "7 min read",
      category: "Market Analysis",
      tags: ["market cycles", "real estate investment", "market analysis", "property trends"]
    },
    {
      id: 4,
      slug: "landlords-guide-property-management-best-practices",
      title: "Landlord's Guide: Property Management Best Practices",
      excerpt: "Successful property management requires more than just collecting rent. Learn best practices for tenant relations, maintenance, and maximizing your rental income.",
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=500&fit=crop",
      author: "Jennifer Martinez",
      date: "February 28, 2024",
      updatedDate: "February 28, 2024",
      readTime: "9 min read",
      category: "Property Management",
      tags: ["property management", "landlord tips", "tenant screening", "rental property"]
    }
  ];

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = ["All", "Buying Tips", "Investment", "Market Analysis", "Property Management"];

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <Helmet>
        <title>Real Estate Blog | Expert Tips for Buyers, Sellers & Investors | EstateGhar</title>
        <meta name="description" content="Expert real estate tips, market insights, and practical advice for home buyers, sellers, investors, and property managers. Stay updated with the latest real estate trends." />
        <meta name="keywords" content="real estate blog, property tips, home buying guide, real estate investment, property management, market analysis" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://estateghar.com/blogs" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Real Estate Blog | Expert Tips & Insights | EstateGhar" />
        <meta property="og:description" content="Expert real estate tips, market insights, and practical advice for buyers, sellers, and investors." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://estateghar.com/blogs" />
        <meta property="og:image" content="https://estateghar.com/blogs-og-image.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Real Estate Blog | Expert Tips & Insights" />
        <meta name="twitter:description" content="Expert real estate tips, market insights, and practical advice for buyers, sellers, and investors." />
        <meta name="twitter:image" content="https://estateghar.com/blogs-og-image.jpg" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-green-600 to-green-700 text-white overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-20 text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <BookOpen className="w-5 h-5" />
              <span className="text-sm font-medium">Real Estate Insights</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Real Estate Blogs
            </h1>
            <p className="text-xl text-green-50 max-w-2xl mx-auto">
              Expert tips, market insights, and practical advice for buyers, sellers, and investors
            </p>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 to-transparent"></div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
          {/* Search and Filter Bar */}
          <div className="mb-12 space-y-4 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search blogs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                aria-label="Search blogs"
              />
              <svg
                className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                    selectedCategory === category
                      ? "bg-green-600 text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  aria-label={`Filter by ${category}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Blog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {filteredBlogs.map((blog) => (
              <Link
                key={blog.id}
                to={`/blog/${blog.slug}`}
                className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full shadow-md">
                      {blog.category}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <time dateTime={blog.date}>{blog.date}</time>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{blog.readTime}</span>
                    </div>
                  </div>

                  <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors line-clamp-2">
                    {blog.title}
                  </h2>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {blog.excerpt}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                        {blog.author.charAt(0)}
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{blog.author}</span>
                    </div>
                    <div className="text-green-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read More
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Empty State */}
          {filteredBlogs.length === 0 && (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No blogs found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
          )}

          {/* Newsletter Signup */}
          <div className="mt-16 bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-3">Subscribe to Our Newsletter</h3>
            <p className="text-green-50 mb-6 max-w-md mx-auto">
              Get the latest real estate tips and insights delivered straight to your inbox
            </p>
            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" action="/subscribe" method="POST">
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
                required
                aria-label="Email address for newsletter"
              />
              <button type="submit" className="px-6 py-2 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors">
                Subscribe
              </button>
            </form>
            <p className="text-xs text-green-200 mt-4">We respect your privacy. Unsubscribe at any time.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Blogs;